import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import dayjs from 'dayjs';

// Create the context
const BookingContext = createContext();

export const useBooking = () => useContext(BookingContext);

export const BookingProvider = ({ children }) => {
  // Core booking states
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  const lastStatusCountsCallRef = useRef(null);

  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    current_page: 1,
    page_size: 50,
    total_pages: 1,
    has_next: false,
    has_previous: false
  });


  // Single booking details state
  const [currentBooking, setCurrentBooking] = useState(null);
  const [bookingActivities, setBookingActivities] = useState([]);
  const [bookingNotes, setBookingNotes] = useState([]);
  const [bookingPayments, setBookingPayments] = useState([]);
  const [bookingDocuments, setBookingDocuments] = useState([]);
  const [bookingCommissions, setBookingCommissions] = useState([]);
  const [bookingCommissionPayments, setBookingCommissionPayments] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs for preventing duplicate calls
  const lastCallRef = useRef(null);
  const currentRequestIdRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Fetch bookings with applied filters
  const fetchBookings = useCallback(async (filters = {}, dateRangeState = null, searchKeyword = null, forceRefresh = false) => {
    // Prevent duplicate calls with better comparison
    const callKey = JSON.stringify({ 
      filters: filters || {}, 
      dateRangeState: dateRangeState ? {
        start_date: dateRangeState.currentStart?.format('YYYY-MM-DD'),
        end_date: dateRangeState.currentEnd?.format('YYYY-MM-DD'),
        filter_id: dateRangeState.filter?.id
      } : null, 
      searchKeyword: searchKeyword || null,
      all_results: filters.all_results || false
    });
    
    // Check if this is the same call as the last one (unless forceRefresh is true)
    if (lastCallRef.current === callKey && !forceRefresh) {
      console.log('Preventing duplicate API call for:', callKey);
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (isLoadingRef.current) {
      console.log('API call already in progress, skipping...');
      return;
    }
    
    // Update the last call reference BEFORE making the call
    lastCallRef.current = callKey;
    
    // Generate a unique request ID for this call
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    console.log('Making API call for:', callKey, 'Request ID:', requestId);

    setLoading(true);
    isLoadingRef.current = true;
    setError(null);

    try {
      // Build the request payload
      const requestPayload = {
        // Always include pagination and sorting
        page: filters.page || 1,
        page_size: filters.page_size || 50,
        sort_field: filters.sort_field || '-updated_at',
        sort_order: filters.sort_order || 'desc',
        is_deleted: filters.is_deleted || false,
        all_results: filters.all_results || false
      };

      // Add search if provided
      if (searchKeyword && typeof searchKeyword === 'string' && searchKeyword.trim()) {
        requestPayload.search = searchKeyword.trim();
      }

      // Add other filters (excluding pagination and sorting fields)
      Object.keys(filters).forEach(key => {
        if (!['page', 'page_size', 'sort_field', 'sort_order','search', 'all_results'].includes(key)) {
          if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
            let filterValue = filters[key];
            
            // Extract values from objects for specific fields
            const fieldsToProcess = ['agents', 'leads', 'projects'];
            if (fieldsToProcess.includes(key) && Array.isArray(filterValue)) {
              filterValue = filterValue.map(item => {
                // If item is an object with 'value' property, extract the value
                if (typeof item === 'object' && item !== null && 'value' in item) {
                  return item.value;
                }
                // Otherwise return as-is
                return item;
              });
            }
            
            // Convert filter values to arrays for backend compatibility
            if (Array.isArray(filterValue)) {
              requestPayload[key] = filterValue;
            } else {
              requestPayload[key] = [filterValue];
            }
          }
        }
      });

      // Add date range if provided
      if (dateRangeState && dateRangeState.currentStart && dateRangeState.currentEnd) {
        requestPayload.date_range = {
          start_date: dateRangeState.currentStart.format('YYYY-MM-DD'),
          end_date: dateRangeState.currentEnd.format('YYYY-MM-DD')
        };
      }

      console.log('API Request Payload:', requestPayload);

      const response = await api.post('/bms/bookings/', requestPayload);
      
      // Check if this is still the current request (prevent stale responses)
      if (currentRequestIdRef.current !== requestId) {
        console.log('Ignoring stale response for request ID:', requestId);
        return;
      }
      
      const responseData = response.data;
      
      // Handle pagination info if provided by backend
      if (responseData.pagination) {
        setPaginationInfo(prev => ({
          ...prev,
          total_count: responseData.pagination.total_count || 0,
          total_pages: responseData.pagination.total_pages || 1,
          has_next: responseData.pagination.has_next || false,
          has_previous: responseData.pagination.has_previous || false,
          current_page: responseData.pagination.current_page || 1,
          page_size: responseData.pagination.page_size || 50
        }));
      }

      const bookingsData = responseData.bookings || responseData;
      console.log('booking ids', bookingsData.map(booking => booking.id || booking.booking_id));
      setBookings(bookingsData);
      setFilteredBookings(bookingsData);
      
      return bookingsData;
    } catch (error) {
      // Check if this is still the current request (prevent stale error handling)
      if (currentRequestIdRef.current !== requestId) {
        console.log('Ignoring stale error for request ID:', requestId);
        return;
      }
      
      console.error('Error fetching bookings:', error);
      
      // Handle the error gracefully
      const errorMessage = error.response?.status === 500 
        ? 'Server error occurred. Please try again later.' 
        : error.message || 'Failed to fetch bookings';
      
      setError(errorMessage);
      
      // Set empty data to prevent UI from crashing
      setBookings([]);
      setFilteredBookings([]);
      
      // Don't reset the last call reference on error to prevent infinite retries
      // Only reset if it's not a server error (5xx)
      if (error.response?.status < 500) {
        lastCallRef.current = null;
      }
      
      // Don't throw the error - instead return an error object that can be checked
      return { 
        error: true, 
        message: errorMessage,
        status: error.response?.status
      };
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, []); // Removed loading dependency to prevent infinite loops

  // Add new booking
  const addNewBooking = async (bookingData) => {
    setLoading(true);
    try {
      const response = await api.post('/bms/bookings/create/', bookingData);
      // Refresh the bookings list after successful creation
      await fetchBookings(filters, null, null, true);
      return response.data;
    } catch (error) {
      console.error('Error adding new booking:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update booking
  const updateBooking = async (id, bookingData) => {
    setLoading(true);
    try {
      const response = await api.put(`/bms/bookings/${id}/`, bookingData);
      // Refresh the bookings list after successful update
      await fetchBookings(filters, null, null, true);
      return response.data;
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete booking
  const deleteBooking = async (id) => {
    setLoading(true);
    try {
      await api.delete(`/bms/bookings/${id}/`);
      // Refresh the bookings list after successful deletion
      await fetchBookings(filters, null, null, true);
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Booking status operations
  const updateBookingStatus = async (bookingId, data) => {
    setLoading(true);
    try {
      const response = await api.patch(`/bms/bookings/${bookingId}/update_status/`, data);
      await fetchBookings(filters, null, null, true);
      return response;
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Single booking operations
  const fetchBookingDetails = async (bookingId) => {
    setBookingLoading(true);
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/`);
      setCurrentBooking(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking details:', error);
      throw error;
    } finally {
      setBookingLoading(false);
    }
  };

  const fetchBookingActivities = async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/activities/`);
      setBookingActivities(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking activities:', error);
      throw error;
    }
  };

  const fetchBookingPayments = async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/payments/`);
      setBookingPayments(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking payments:', error);
      throw error;
    }
  };

  const fetchBookingDocuments = async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/documents/`);
      setBookingDocuments(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking documents:', error);
      throw error;
    }
  };

  const fetchBookingCommissions = async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/commissions/`);
      setBookingCommissions(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking commissions:', error);
      throw error;
    }
  };

  const fetchBookingCommissionPayments = async (bookingId) => {
    try {
      const response = await api.get(`/bms/bookings/${bookingId}/commission-payments/`);
      setBookingCommissionPayments(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking commission payments:', error);
      throw error;
    }
  };

  // Add operations
  const addBookingPayment = async (bookingId, paymentData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/payments/`, paymentData);
      await fetchBookingPayments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error adding booking payment:', error);
      throw error;
    }
  };

  const addBookingDocument = async (bookingId, documentData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/documents/`, documentData);
      await fetchBookingDocuments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error adding booking document:', error);
      throw error;
    }
  };

  const addBookingCommission = async (bookingId, commissionData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/commissions/`, commissionData);
      await fetchBookingCommissions(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error adding booking commission:', error);
      throw error;
    }
  };

  const addBookingCommissionPayment = async (bookingId, paymentData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/commission-payments/`, paymentData);
      await fetchBookingCommissionPayments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error adding booking commission payment:', error);
      throw error;
    }
  };

  // Update operations
  const updateBookingPayment = async (bookingId, paymentId, paymentData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/payments/${paymentId}/`, paymentData);
      await fetchBookingPayments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking payment:', error);
      throw error;
    }
  };

  const updateBookingDocument = async (bookingId, documentId, documentData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/documents/${documentId}/`, documentData);
      await fetchBookingDocuments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking document:', error);
      throw error;
    }
  };

  const updateBookingCommission = async (bookingId, commissionId, commissionData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/commissions/${commissionId}/`, commissionData);
      await fetchBookingCommissions(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking commission:', error);
      throw error;
    }
  };

  const updateBookingCommissionPayment = async (bookingId, paymentId, paymentData) => {
    try {
      const response = await api.put(`/bms/bookings/${bookingId}/commission-payments/${paymentId}/`, paymentData);
      await fetchBookingCommissionPayments(bookingId);
      return response.data;
    } catch (error) {
      console.error('Error updating booking commission payment:', error);
      throw error;
    }
  };

  // Delete operations
  const deleteBookingPayment = async (bookingId, paymentId) => {
    try {
      await api.delete(`/bms/bookings/${bookingId}/payments/${paymentId}/`);
      await fetchBookingPayments(bookingId);
    } catch (error) {
      console.error('Error deleting booking payment:', error);
      throw error;
    }
  };

  const deleteBookingDocument = async (bookingId, documentId) => {
    try {
      await api.delete(`/bms/bookings/${bookingId}/documents/${documentId}/`);
      await fetchBookingDocuments(bookingId);
    } catch (error) {
      console.error('Error deleting booking document:', error);
      throw error;
    }
  };

  const deleteBookingCommission = async (bookingId, commissionId) => {
    try {
      await api.delete(`/bms/bookings/${bookingId}/commissions/${commissionId}/`);
      await fetchBookingCommissions(bookingId);
    } catch (error) {
      console.error('Error deleting booking commission:', error);
      throw error;
    }
  };

  const deleteBookingCommissionPayment = async (bookingId, paymentId) => {
    try {
      await api.delete(`/bms/bookings/${bookingId}/commission-payments/${paymentId}/`);
      await fetchBookingCommissionPayments(bookingId);
    } catch (error) {
      console.error('Error deleting booking commission payment:', error);
      throw error;
    }
  };

  // Other customer operations
  const updateAdditionalCustomer = async (bookingId, customerData) => {
    try {
      const response = await api.post(`/bms/bookings/${bookingId}/additional-customers/`, {'additional_customers': customerData});
      if(response.status === 200) {
        await fetchBookingDetails(bookingId);
      }
      return response.data;
    } catch (error) {
      console.error('Error updating additional customer:', error);
      throw error;
    }
  };

  // Update booking finance
  const updateBookingAmount = async (bookingId, financeData) => {
    try {
      const response = await api.patch(`/bms/bookings/${bookingId}/booking_amount/`, financeData);
      return response
    }
    catch (error) {
      console.error('Error updating booking finance:', error);
      throw error;
    }
  };

  // Booking status counts
  const fetchBookingStatusCounts = async (dateRangeState = null) => {    
    // Check if this is a duplicate API call
    const currentDateRange = dateRangeState ? {
      start_date: dateRangeState.currentStart?.format('YYYY-MM-DD'),
      end_date: dateRangeState.currentEnd?.format('YYYY-MM-DD'),
      all_results: dateRangeState.all_results || dateRangeState.filter?.id === 'All'
    } : null;
    
    if (JSON.stringify(currentDateRange) === JSON.stringify(lastStatusCountsCallRef.current)) {
      return statusCounts; // Return current status counts without making API call
    }
    
    // Update the last call reference
    lastStatusCountsCallRef.current = currentDateRange;
    
    
    try {
      // Determine the date range to use
      let startDate;
      let endDate;

      if (dateRangeState && dateRangeState.currentStart && dateRangeState.currentEnd) {
        // Use provided date range if valid
        startDate = dateRangeState.currentStart;
        endDate = dateRangeState.currentEnd;
      } else {
        // Default to current date
        startDate = dayjs();
        endDate = dayjs(); // Today
      }

      // Create the request payload for POST request
      const requestPayload = {
        // Date range
        date_range: {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD')
        },
        all_results: dateRangeState.all_results || dateRangeState.filter?.id === 'All'
      };

      // Make the POST API request
      const response = await api.post('/bms/bookings/status-counts/', requestPayload);
      
      if (response && response.data) {
        return response.data;
      } else {
        console.error('Invalid status counts API response structure:', response);
        return {};
      }
    } catch (error) {
      
      // Log detailed error information
      if (error.response) {
        console.error('Error response data:', error.response.data);
      } else if (error.request) {
        console.error('Error request:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      return {};
    }
  };

  // Context value
  const value = {
    // States
    bookings: filteredBookings,
    allBookings: bookings,
    filters,
    loading,
    paginationInfo,
    currentBooking,
    bookingActivities,
    bookingNotes,
    bookingPayments,
    bookingDocuments,
    bookingCommissions,
    bookingCommissionPayments,
    bookingLoading,
    error,

    // Setters
    setPaginationInfo,
    setFilters,
    setBookings,
    setStatusCounts,
    // Core operations
    fetchBookings,
    addNewBooking,
    updateBooking,
    deleteBooking,
    updateBookingStatus,

    // Single booking operations
    fetchBookingDetails,
    fetchBookingActivities,
    fetchBookingPayments,
    fetchBookingDocuments,
    fetchBookingCommissions,
    fetchBookingCommissionPayments,

    // Add operations
    addBookingPayment,
    addBookingDocument,
    addBookingCommission,
    addBookingCommissionPayment,

    // Update operations
    updateBookingPayment,
    updateBookingDocument,
    updateBookingCommission,
    updateBookingCommissionPayment,

    // Delete operations
    deleteBookingPayment,
    deleteBookingDocument,
    deleteBookingCommission,
    deleteBookingCommissionPayment,

    // Other customer operations
    updateAdditionalCustomer,

    // Update booking amount
    updateBookingAmount,

    // Booking status counts
    fetchBookingStatusCounts,
    statusCounts,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};
